import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  FolderOpen, 
  Plus, 
  Upload,
  FileText,
  Image as ImageIcon,
  File,
  Trash2,
  Eye,
  Download,
  Send,
  Loader2,
  X,
  ChevronRight,
  FolderPlus,
  Clock,
  CheckCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DashboardLayout from "@/components/DashboardLayout";

import { API_URL } from "@/utils/api";

const FOLDER_CATEGORIES = [
  { id: "tax_documents", name: "Tax Documents", icon: "📄" },
  { id: "past_returns", name: "Past Returns", icon: "📋" },
  { id: "compliance_documents", name: "Compliance Documents", icon: "📜" },
  { id: "seller_permits", name: "Seller Permits", icon: "🏪" },
  { id: "others", name: "Others", icon: "📁" }
];

const ALLOWED_EXTENSIONS = ".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg";

export default function DocuHubPage({ user }) {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    folder: "tax_documents",
    description: ""
  });
  const [sendForm, setSendForm] = useState({
    email: "",
    message: ""
  });
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFolders();
    fetchDocuments();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [selectedFolder]);

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/docuhub/folders`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders || []);
      }
    } catch (err) {
      console.error("Error fetching folders:", err);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      let url = `${API_URL}/api/docuhub/documents`;
      if (selectedFolder) {
        url += `?folder=${selectedFolder}`;
      }
      
      const response = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${ALLOWED_EXTENSIONS}`);
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", uploadForm.folder);
      formData.append("description", uploadForm.description);
      
      const response = await fetch(`${API_URL}/api/docuhub/upload`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to upload document");
      }
      
      toast.success("Document uploaded successfully!");
      setShowUploadDialog(false);
      setUploadForm({ folder: "tax_documents", description: "" });
      fetchDocuments();
      fetchFolders();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;
    
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/docuhub/documents/${selectedDocument.document_id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error("Failed to delete document");
      }
      
      toast.success("Document deleted");
      setShowDeleteDialog(false);
      setSelectedDocument(null);
      fetchDocuments();
      fetchFolders();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSendFolder = async () => {
    if (!selectedFolder || !sendForm.email) {
      toast.error("Please select a folder and enter an email");
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/docuhub/send-folder`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          folder: selectedFolder,
          recipient_email: sendForm.email,
          message: sendForm.message
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to send folder");
      }
      
      toast.success(`Folder sent to ${sendForm.email}`);
      setShowSendDialog(false);
      setSendForm({ email: "", message: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (["pdf"].includes(fileType)) return <FileText className="w-5 h-5 text-red-500" />;
    if (["png", "jpg", "jpeg"].includes(fileType)) return <ImageIcon className="w-5 h-5 text-blue-500" />;
    if (["xlsx", "xls", "csv"].includes(fileType)) return <File className="w-5 h-5 text-green-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const getPreviewUrl = (doc) => {
    const token = localStorage.getItem("auth_token");
    return `${API_URL}/api/docuhub/documents/${doc.document_id}/view?token=${token}`;
  };

  const canPreview = (fileType) => {
    return ["pdf", "png", "jpg", "jpeg"].includes(fileType?.toLowerCase());
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFolderByCategory = (categoryId) => {
    const folder = folders.find(f => f.folder === categoryId);
    return folder?.count || 0;
  };

  const filteredDocuments = selectedFolder 
    ? documents.filter(d => d.folder === selectedFolder)
    : documents;

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">DocuHub</h1>
            <p className="text-gray-500 mt-1">
              Organize and share your important documents with your finance team
            </p>
          </div>
          <div className="flex gap-2">
            {selectedFolder && (
              <Button 
                variant="outline"
                className="gap-2"
                onClick={() => setShowSendDialog(true)}
                data-testid="send-folder-btn"
              >
                <Send className="w-4 h-4" />
                Send Folder
              </Button>
            )}
            <Button 
              className="bg-[#2563eb] hover:bg-[#1d4ed8] gap-2"
              onClick={() => setShowUploadDialog(true)}
              data-testid="upload-document-btn"
            >
              <Upload className="w-4 h-4" />
              Upload Document
            </Button>
          </div>
        </div>

        {/* Folder Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {FOLDER_CATEGORIES.map((category) => {
            const count = getFolderByCategory(category.id);
            const isSelected = selectedFolder === category.id;
            
            return (
              <Card 
                key={category.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isSelected 
                    ? 'ring-2 ring-[#2563eb] bg-blue-50' 
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelectedFolder(isSelected ? null : category.id)}
                data-testid={`folder-${category.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{category.icon}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{category.name}</p>
                      <p className="text-sm text-gray-500">{count} document{count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Selected Folder Info */}
        {selectedFolder && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FolderOpen className="w-4 h-4" />
            <span>Viewing: </span>
            <Badge variant="secondary">
              {FOLDER_CATEGORIES.find(f => f.id === selectedFolder)?.name}
            </Badge>
            <button 
              onClick={() => setSelectedFolder(null)}
              className="text-[#2563eb] hover:underline ml-2"
            >
              Show All
            </button>
          </div>
        )}

        {/* Documents List */}
        <Card className="border border-gray-200">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selectedFolder 
                ? FOLDER_CATEGORIES.find(f => f.id === selectedFolder)?.name 
                : "All Documents"
              }
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="py-12 text-center">
                <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No documents in this folder</p>
                <Button 
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] gap-2"
                  onClick={() => setShowUploadDialog(true)}
                >
                  <Upload className="w-4 h-4" />
                  Upload Document
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">File</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Folder</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Size</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Uploaded</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredDocuments.map((doc) => (
                      <tr key={doc.document_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {getFileIcon(doc.file_type)}
                            <div>
                              <p className="font-medium text-gray-900 truncate max-w-[200px]">
                                {doc.original_filename}
                              </p>
                              {doc.description && (
                                <p className="text-sm text-gray-500 truncate max-w-[200px]">
                                  {doc.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary">
                            {FOLDER_CATEGORIES.find(f => f.id === doc.folder)?.name || doc.folder}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatFileSize(doc.file_size)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 
                           doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {canPreview(doc.file_type) && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setSelectedDocument(doc);
                                  setShowPreviewDialog(true);
                                }}
                                data-testid={`preview-${doc.document_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                const token = localStorage.getItem("auth_token");
                                window.open(`${API_URL}/api/docuhub/documents/${doc.document_id}/view?token=${token}`, '_blank');
                              }}
                              data-testid={`download-${doc.document_id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                setSelectedDocument(doc);
                                setShowDeleteDialog(true);
                              }}
                              data-testid={`delete-${doc.document_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Secure Document Sharing</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Documents uploaded here are securely stored and can be accessed by your assigned admin/finance team members.
                  Use the "Send Folder" feature to notify them about new uploads.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Choose a folder and upload your document
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Folder</Label>
              <Select
                value={uploadForm.folder}
                onValueChange={(value) => setUploadForm({ ...uploadForm, folder: value })}
              >
                <SelectTrigger data-testid="folder-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FOLDER_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of the document"
                data-testid="document-description"
              />
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept={ALLOWED_EXTENSIONS}
                className="hidden"
              />
              <Button 
                variant="outline" 
                className="w-full gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="select-file-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Select File
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Allowed: PDF, XLS, XLSX, CSV, PNG, JPG, JPEG (max 10MB)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {selectedDocument && getFileIcon(selectedDocument.file_type)}
              {selectedDocument?.original_filename}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-gray-100 rounded-lg overflow-hidden">
            {selectedDocument && canPreview(selectedDocument.file_type) ? (
              selectedDocument.file_type === 'pdf' ? (
                <iframe
                  src={getPreviewUrl(selectedDocument)}
                  className="w-full h-full min-h-[500px]"
                  style={{ border: 'none' }}
                  title={selectedDocument.original_filename}
                />
              ) : (
                <div className="w-full h-full overflow-auto p-4 flex items-center justify-center">
                  <img 
                    src={getPreviewUrl(selectedDocument)} 
                    alt={selectedDocument.original_filename}
                    className="max-w-full h-auto object-contain"
                  />
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px]">
                <File className="w-16 h-16 text-gray-400 mb-4" />
                <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                <Button
                  onClick={() => window.open(getPreviewUrl(selectedDocument), '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            )}
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={() => window.open(getPreviewUrl(selectedDocument), '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Folder Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Folder to Finance Team</DialogTitle>
            <DialogDescription>
              Notify your finance team about documents in this folder
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                <strong>Folder:</strong> {FOLDER_CATEGORIES.find(f => f.id === selectedFolder)?.name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Documents:</strong> {filteredDocuments.length} file(s)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={sendForm.email}
                onChange={(e) => setSendForm({ ...sendForm, email: e.target.value })}
                placeholder="finance@company.com"
                data-testid="send-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Input
                value={sendForm.message}
                onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                placeholder="Please review these documents"
                data-testid="send-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-[#2563eb] hover:bg-[#1d4ed8]"
              onClick={handleSendFolder}
              disabled={sending || !sendForm.email}
              data-testid="send-folder-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedDocument?.original_filename}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
